import React, { useEffect, useRef, useState } from "react";
import AddNewsForm from "./AddNewsForm";
import EditNewsForm from "./EditNewsForm";
import NewsListAdmin from "./NewsListAdmin";
import ParsedNewsList from "./ParsedNewsList";
import AdminUsersPanel from "./AdminUsersPanel";
import { useNews } from "../components/NewsContext";
import { useUser } from "../components/UserContext";
import {
  ADMIN_TABS,
  canAccessAdminTab,
  canDeleteNews,
  canManageUsers,
} from "../constants/userRoles";
import {
  ADMIN_NEWS_PAGE_SIZE,
  getAdminNewsStateAfterDeletion,
  getCachedAdminNewsPage,
  loadAdminNewsCount,
  loadAdminNewsPage,
} from "../services/adminNewsPaginationService";
import "../styles/AdminPanel.css";

const AdminPanel = () => {
  const { userData } = useUser();
  const role = userData?.role;
  const isAdmin = canManageUsers(userData?.role);
  const {
    addNews,
    deleteNews,
    editNews,
  } = useNews();

  const [adminNews, setAdminNews] = useState([]);
  const [adminNewsInitialLoading, setAdminNewsInitialLoading] = useState(false);
  const [adminNewsPageLoading, setAdminNewsPageLoading] = useState(false);
  const [adminNewsRefreshing, setAdminNewsRefreshing] = useState(false);
  const [adminNewsError, setAdminNewsError] = useState("");
  const [adminNewsPage, setAdminNewsPage] = useState(1);
  const [adminNewsTotal, setAdminNewsTotal] = useState(null);
  const [adminNewsHasNext, setAdminNewsHasNext] = useState(false);
  const adminNewsCursorsRef = useRef({ 1: null });
  const adminNewsMountedAt = useRef(performance.now());

  const [parsedNews, setParsedNews] = useState([]);
  const [editingNews, setEditingNews] = useState(null);
  const [activeTab, setActiveTab] = useState(ADMIN_TABS.ADD_NEWS);

  useEffect(() => {
    if (!canAccessAdminTab(role, activeTab)) {
      setActiveTab(ADMIN_TABS.ADD_NEWS);
    }
  }, [activeTab, role]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.debug(
        `[ADMIN NEWS MOUNT] ${adminNewsMountedAt.current.toFixed(1)}ms`
      );
    }
  }, []);

  useEffect(() => {
    let alive = true;

    const fetchAdminNews = async () => {
      if (activeTab !== ADMIN_TABS.SITE_NEWS) return;

      const cachedPage = getCachedAdminNewsPage({
        page: adminNewsPage,
        pageSize: ADMIN_NEWS_PAGE_SIZE,
      });
      const cursor = adminNewsCursorsRef.current[adminNewsPage];

      setAdminNewsError("");

      if (cachedPage) {
        setAdminNews(cachedPage.news);
        setAdminNewsHasNext(cachedPage.hasNext);
        setAdminNewsInitialLoading(false);
        setAdminNewsPageLoading(false);
        setAdminNewsRefreshing(true);
      } else {
        setAdminNews([]);
        setAdminNewsInitialLoading(adminNewsPage === 1);
        setAdminNewsPageLoading(adminNewsPage > 1);
        setAdminNewsRefreshing(false);
      }

      try {
        const pageResult = await loadAdminNewsPage({
          page: adminNewsPage,
          pageSize: ADMIN_NEWS_PAGE_SIZE,
          cursor,
        });

        if (alive) {
          setAdminNews(pageResult.news);
          setAdminNewsHasNext(pageResult.hasNext);
          adminNewsCursorsRef.current[adminNewsPage + 1] =
            pageResult.lastVisible;

          if (process.env.NODE_ENV === "development") {
            console.debug(
              `[ADMIN NEWS FIRST RENDER] page=${adminNewsPage} `
              + `documents=${pageResult.news.length} `
              + `duration=${(
                performance.now() - adminNewsMountedAt.current
              ).toFixed(1)}ms`
            );
          }
        }
      } catch (error) {
        if (alive) {
          setAdminNewsError("Не вдалося завантажити новини");
        }
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading admin news:", error);
        }
      } finally {
        if (alive) {
          setAdminNewsInitialLoading(false);
          setAdminNewsPageLoading(false);
          setAdminNewsRefreshing(false);
        }
      }
    };

    fetchAdminNews();

    return () => {
      alive = false;
    };
  }, [activeTab, adminNewsPage]);

  useEffect(() => {
    if (
      activeTab !== ADMIN_TABS.SITE_NEWS ||
      adminNewsTotal !== null
    ) {
      return undefined;
    }

    let alive = true;
    loadAdminNewsCount()
      .then((count) => {
        if (alive) setAdminNewsTotal(count);
      })
      .catch((countError) => {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading admin news count:", countError);
        }
      });

    return () => {
      alive = false;
    };
  }, [activeTab, adminNewsTotal]);

  const handleEditClick = (news) => {
    setEditingNews(news);
  };

  const handleCloseEdit = () => {
    setEditingNews(null);
  };

  const handleEditNews = async (updatedNews) => {
    await editNews(updatedNews);

    setAdminNews((prev) =>
      prev.map((item) => (item.id === updatedNews.id ? updatedNews : item))
    );

    setEditingNews(null);
  };

  const handleDeleteNews = async (id) => {
    if (!canDeleteNews(userData?.role)) {
      return;
    }

    await deleteNews(id);

    const deletionState = getAdminNewsStateAfterDeletion({
      news: adminNews,
      deletedId: id,
      currentPage: adminNewsPage,
      totalCount: adminNewsTotal,
    });
    setAdminNewsTotal(deletionState.totalCount);

    if (deletionState.currentPage !== adminNewsPage) {
      setAdminNewsPage(deletionState.currentPage);
    } else {
      setAdminNews(deletionState.news);
    }

    if (editingNews && editingNews.id === id) {
      setEditingNews(null);
    }
  };

  const handleAddNews = async (news) => {
    await addNews(news);
    adminNewsCursorsRef.current = { 1: null };
    setAdminNewsPage(1);
    setAdminNewsTotal(null);
  };

  const handleAdminNewsPageChange = (page) => {
    if (page < 1 || page === adminNewsPage) return;
    setAdminNewsPage(page);
  };

  const handleDeleteParsedNews = (id) => {
    setParsedNews((prev) => prev.filter((news) => news.id !== id));
  };

  const handleAddParsedNewsToSite = async (news) => {
    await addNews(news);

    setParsedNews((prev) => prev.filter((item) => item.id !== news.id));
    adminNewsCursorsRef.current = { 1: null };
    setAdminNews([]);
    setAdminNewsPage(1);
    setAdminNewsTotal(null);
    setActiveTab(ADMIN_TABS.SITE_NEWS);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case ADMIN_TABS.ADD_NEWS:
        return <AddNewsForm onAdd={handleAddNews} />;

      case ADMIN_TABS.USERS:
        return isAdmin
          ? <AdminUsersPanel view="users" />
          : <p>У вас немає доступу до цього розділу</p>;

      case ADMIN_TABS.EMPLOYEES:
        return isAdmin
          ? <AdminUsersPanel view="employees" />
          : <p>У вас немає доступу до цього розділу</p>;

      case ADMIN_TABS.SITE_NEWS:
        return (
          <>
            {adminNewsError && adminNews.length === 0 && (
              <p className="admin-news-error">{adminNewsError}</p>
            )}
            <NewsListAdmin
              newsList={adminNews}
              onEdit={handleEditClick}
              onDelete={isAdmin ? handleDeleteNews : null}
              initialLoading={adminNewsInitialLoading}
              pageLoading={adminNewsPageLoading}
              isRefreshing={adminNewsRefreshing}
              currentPage={adminNewsPage}
              totalPages={
                adminNewsTotal === null
                  ? null
                  : Math.max(
                    1,
                    Math.ceil(adminNewsTotal / ADMIN_NEWS_PAGE_SIZE)
                  )
              }
              hasNext={adminNewsHasNext}
              onPageChange={handleAdminNewsPageChange}
            />
          </>
        );

      case ADMIN_TABS.PARSED_NEWS:
        if (!canAccessAdminTab(role, ADMIN_TABS.PARSED_NEWS)) {
          return <p>У вас немає доступу до цього розділу</p>;
        }

        return (
          <ParsedNewsList
            parsedNews={parsedNews}
            onEdit={handleEditClick}
            onDelete={handleDeleteParsedNews}
            onAddToSite={handleAddParsedNewsToSite}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-header">
        <h1 className="admin-panel-title">Адмін-панель BigSport</h1>
      </div>

      <div className="admin-tabs-wrap">
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === ADMIN_TABS.ADD_NEWS ? "active" : ""}`}
            onClick={() => setActiveTab(ADMIN_TABS.ADD_NEWS)}
          >
            Додати новину
          </button>

          {isAdmin && (
            <>
              <button
                className={`admin-tab ${activeTab === ADMIN_TABS.USERS ? "active" : ""}`}
                onClick={() => setActiveTab(ADMIN_TABS.USERS)}
              >
                Користувачі
              </button>

              <button
                className={`admin-tab ${activeTab === ADMIN_TABS.EMPLOYEES ? "active" : ""}`}
                onClick={() => setActiveTab(ADMIN_TABS.EMPLOYEES)}
              >
                Працівники
              </button>
            </>
          )}

          <button
            className={`admin-tab ${activeTab === ADMIN_TABS.SITE_NEWS ? "active" : ""}`}
            onClick={() => setActiveTab(ADMIN_TABS.SITE_NEWS)}
          >
            Новини сайту
          </button>

          {canAccessAdminTab(role, ADMIN_TABS.PARSED_NEWS) && (
            <button
              className={`admin-tab ${activeTab === ADMIN_TABS.PARSED_NEWS ? "active" : ""}`}
              onClick={() => setActiveTab(ADMIN_TABS.PARSED_NEWS)}
            >
              Парсинговані новини
            </button>
          )}
        </div>
      </div>

      {editingNews && (
        <EditNewsForm
          news={editingNews}
          onEdit={handleEditNews}
          onClose={handleCloseEdit}
        />
      )}

      <div className="admin-tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminPanel;
